import { useMemo } from 'react';

import { useEstimationModelQueryActions } from '@/api/hooks/estimationModelQueryActions';
import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import { useMethodologyQueryActions } from '@/api/hooks/methodologyQueryActions';
import { useProjectTypesQueryActions } from '@/api/hooks/projectTypesQueryActions';
import { useUserQueryActions } from '@/api/hooks/userQueryActions';
import type {
  EstimationModelResponse,
  EstimationResponse,
  EstimationUserResponse,
  MethodologyResponse,
  ProjectResponse,
  ProjectTypeResponse,
  ProjectWithEstimationResponse,
} from '@/api/schema/data-contracts';

export interface EstimationProjectUsers {
  estimation: EstimationResponse;
  project: ProjectResponse;
  estimationModel: EstimationModelResponse;
  methodologies: MethodologyResponse[];
  projectTypes: ProjectTypeResponse[];
  users: EstimationUserResponse[];
}

export const useGetEstimationProjectUsers = (
  estimationId: string,
): { isLoading: boolean; data: EstimationProjectUsers | undefined } => {
  const { useGetEstimation } = useEstimationQueryActions();
  const { data: estimationResponse, isLoading: isLoadingEstimation } = useGetEstimation(
    estimationId,
  ) as { data: ProjectWithEstimationResponse | undefined; isLoading: boolean };

  const { useGetEstimationModel: useGetEstimationModelDetail } = useEstimationModelQueryActions();
  const { data: estimationModel, isLoading: isLoadingEstimationModel } =
    useGetEstimationModelDetail(
      estimationResponse?.estimation.estimationModelId,
      estimationResponse?.estimation.cultureName,
    ) as { data: EstimationModelResponse | undefined; isLoading: boolean };

  const { useGetMethodologies } = useMethodologyQueryActions();
  const { data: methodologies, isLoading: isLoadingMethodologies } = useGetMethodologies(
    estimationResponse?.estimation.cultureName,
    {
      methodologyIds: estimationModel?.methodologyIds,
      onlyCurrent: false,
    },
    !!estimationModel && !!estimationResponse,
  ) as { data: MethodologyResponse[] | undefined; isLoading: boolean };

  const { useGetUsersByEstimationId } = useUserQueryActions();
  const { data: users = [], isLoading: isLoadingUsers } = useGetUsersByEstimationId(
    estimationId,
  ) as { data: EstimationUserResponse[]; isLoading: boolean };

  const { useGetProjectTypes } = useProjectTypesQueryActions();
  const { data: projectTypes, isLoading: isLoadingProjectTypes } = useGetProjectTypes(
    estimationResponse?.estimation.cultureName,
  ) as { data: ProjectTypeResponse[] | undefined; isLoading: boolean };

  const isLoading =
    isLoadingEstimation ||
    isLoadingEstimationModel ||
    isLoadingMethodologies ||
    isLoadingProjectTypes ||
    isLoadingUsers;

  const result = useMemo(() => {
    if (isLoading) {
      return {
        isLoading: true,
        data: undefined,
      };
    }

    if (
      estimationResponse === undefined ||
      estimationModel === undefined ||
      methodologies === undefined ||
      projectTypes === undefined ||
      users === undefined
    ) {
      return {
        isLoading: false,
        data: undefined,
      };
    }

    return {
      isLoading: false,
      data: {
        estimation: estimationResponse.estimation,
        project: estimationResponse.project,
        estimationModel,
        methodologies,
        projectTypes,
        users,
      },
    };
  }, [estimationModel, estimationResponse, isLoading, methodologies, projectTypes, users]);

  return result;
};
